import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { UserService } from '../../services/user.service';
import {
    Campaign,
    CampaignConfig,
    CampaignOrderPayload,
    CampaignReportFile,
    CampaignStatus,
    LocationSuggestion,
    Order,
    OrderStatus,
    PricingBreakdown
} from '../models/youtube-ads.model';

const VIDEO_RATING_CODE: { [label: string]: number } = {
    'Universal': 0,
    'Parental Guidance': 1,
    'Adult': 2
};

const THUMBNAIL_RATING_CODE: { [label: string]: number } = {
    'Non-Explicit': 0,
    'Explicit': 1
};

export interface DolbySubmitPayload {
    songSlug: string;
    albumSlug?: string;
    audioFile?: File;
    audioLink?: string;
    dolbyId?: number;
    notes?: string;
}

export interface SpotifyVideoSubmitPayload {
    songSlug: string;
    albumSlug?: string;
    videoLink: string;
    starCast: string;
    videoRating: string;
    thumbnailRating: string;
    thumbnailFile: File;
    spotifyVideoId: number;
}

function mapReportFiles(reports: any): CampaignReportFile[] {
    return (reports || []).map((r: any, i: number) => ({
        id: r.id != null ? String(r.id) : String(i),
        name: r.name || r.reportName,
        uploadedOn: r.uploadedOn || r.uploaded_on,
        fileUrl: r.fileUrl || r.downloadUrl || r.file_url
    }));
}

function mapCampaignStatus(raw: any): CampaignStatus {
    const normalized = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (normalized === 'live') {
        return 'Live';
    }
    if (normalized === 'completed') {
        return 'Completed';
    }
    return 'In Process';
}

function campaignTypeLabel(raw: any): string {
    const normalized = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (['youtube', 'youtube_ads', 'youtube_promotion'].includes(normalized)) {
        return 'Youtube Ad Campaign';
    }
    return raw;
}

function mapCampaign(c: any): Campaign {
    return {
        id: c.id != null ? String(c.id) : undefined,
        orderId: c.orderId || c.order_id,
        campaignName: c.campaignName || c.campaign_name,
        campaignType: campaignTypeLabel(c.campaignType || c.campaign_type),
        songName: c.songName || c.song_name,
        budget: c.budget,
        durationDays: c.durationDays || c.duration_days,
        status: mapCampaignStatus(c.status),
        createdOn: c.createdOn || c.created_on,
        reports: mapReportFiles(c.reports)
    };
}

function unwrapApiResult(res: any): any {
    if (res && typeof res === 'object') {
        if (res.result !== undefined) {
            return res.result;
        }
        if (res.data !== undefined) {
            return res.data;
        }
    }
    return res;
}

function unwrapList(res: any): any[] {
    if (Array.isArray(res)) {
        return res;
    }
    return (res && (res.data || res.results)) || [];
}

function numericField(v: any): number {
    if (v == null || v === '') {
        return undefined;
    }
    const n = typeof v === 'number' ? v : parseFloat(v);
    return isNaN(n) ? undefined : n;
}

function mapOrderStatus(raw: any): OrderStatus {
    if (typeof raw === 'string' && raw.toLowerCase() === 'pending') {
        return 'Pending Payment';
    }
    if (typeof raw === 'string' && ['paid', 'success', 'completed'].includes(raw.toLowerCase())) {
        return 'In Process';
    }
    return 'Pending Payment';
}

function mapOrder(res: any): Order {
    const hasOrderKey = res && typeof res === 'object' && res.order && typeof res.order === 'object';
    const envelope = hasOrderKey ? res : { order: res };
    const o = envelope.order || {};
    const id = o.order_no ?? o.orderId ?? o.order_id ?? o.id;
    const adSpend = numericField(o.base_amount ?? o.adSpend);
    const managementFee = numericField(o.management_fee ?? o.managementFee);
    const gst = numericField(o.gst_amount ?? o.gst);
    const managementFeePercent = numericField(o.management_fee_percentage ?? o.managementFeePercent);
    const gstPercent = numericField(o.gst_percentage ?? o.gstPercent ?? envelope.product?.gst_percentage);
    const pricing: PricingBreakdown = {
        adSpend,
        managementFeePercent,
        managementFee,
        gstPercent,
        gst,
        total: numericField(o.total_amount ?? o.orderAmount ?? o.total)
    };
    return {
        orderId: id != null ? String(id) : undefined,
        service: o.service || campaignTypeLabel(envelope.product?.product_name) || 'YouTube Ads',
        campaignName: o.campaignName || o.campaign_name || envelope.growth_tool?.song_name,
        songName: o.songName || o.song_name || envelope.growth_tool?.song_name
            || envelope.items?.[0]?.song_name || envelope.order_item?.song_name,
        orderAmount: pricing.total,
        orderDate: o.orderDate || o.order_date || o.created_date || o.created,
        invoiceUrl: o.invoiceUrl || o.invoice_url,
        status: mapOrderStatus(o.payment_status ?? o.status),
        pricing,
        paymentStatus: o.payment_status,
        transactionId: o.transaction_id,
        paymentGateway: o.payment_gateway,
        paidAt: o.paid_at,
        productName: campaignTypeLabel(envelope.product?.product_name || envelope.product?.product_code)
    };
}

function mapConfig(c: any): CampaignConfig {
    return {
        minBudget: c.minBudget ?? c.min_budget,
        maxBudget: c.maxBudget ?? c.max_budget,
        minDurationDays: c.minDurationDays ?? c.min_duration_days,
        maxDurationDays: c.maxDurationDays ?? c.max_duration_days
    };
}

@Injectable({
    providedIn: 'root'
})
export class GrowthToolsDataService {

    apiBase: String = 'https://sdapi.songdew.com/apis';

    constructor(private http: HttpClient, private user: UserService, private api: ApiService) { }

    private authHeaders(): HttpHeaders {
        return new HttpHeaders().set('Authorization', this.user.token);
    }

    private fetchCampaignReportsList(code?: string): Observable<any> {
        let params = new HttpParams();
        if (code) {
            params = params.set('code', code);
        }
        return this.http.get(this.apiBase + '/growth_tools/campaign_reports', {
            params,
            headers: this.authHeaders()
        });
    }

    private fetchCampaignReportDetail(campaignId: string): Observable<any> {
        const params = new HttpParams().set('campaign_id', campaignId);
        return this.http.get(this.apiBase + '/growth_tools/campaign_reports', {
            params,
            headers: this.authHeaders()
        });
    }

    private postCampaignOrder(payload: CampaignOrderPayload): Observable<any> {
        return this.http.post(this.apiBase + '/growth_tools/youtube_ads/campaigns', payload, {
            headers: this.authHeaders()
        });
    }

    private fetchDolbyPrice(): Observable<any> {
        return this.http.get(this.apiBase + '/growth_tools/dolby/get_price', {
            headers: this.authHeaders()
        });
    }

    private postDolbySubmission(formData: FormData): Observable<any> {
        return this.http.post(this.apiBase + '/growth_tools/dolby/save/', formData, {
            headers: this.authHeaders()
        });
    }

    private fetchDolbyStatus(songSlug: string, albumSlug?: string): Observable<any> {
        let params = new HttpParams().set('song_slug', songSlug);
        if (albumSlug) {
            params = params.set('album_slug', albumSlug);
        }
        return this.http.get(this.apiBase + '/growth_tools/get_dolby_status/', {
            params,
            headers: this.authHeaders()
        });
    }

    private fetchSpotifyVideoPrice(): Observable<any> {
        return this.http.get(this.apiBase + '/growth_tools/get_price', {
            headers: this.authHeaders()
        });
    }

    private postSpotifyVideoSubmission(formData: FormData): Observable<any> {
        return this.http.post(this.apiBase + '/growth_tools/save/', formData, {
            headers: this.authHeaders()
        });
    }

    private fetchSpotifyVideoStatus(songSlug: string, albumSlug?: string): Observable<any> {
        let params = new HttpParams().set('song_slug', songSlug);
        if (albumSlug) {
            params = params.set('album_slug', albumSlug);
        }
        return this.http.get(this.apiBase + '/growth_tools/get_spotify_video_status/', {
            params,
            headers: this.authHeaders()
        });
    }

    private fetchOrder(orderId: string): Observable<any> {
        return this.http.get(this.apiBase + '/growth_tools/youtube_ads/orderinfo/' + orderId.toUpperCase() + '/', {
            headers: this.authHeaders()
        });
    }

    private fetchBudgetConfig(budget?: number): Observable<any> {
        let params = new HttpParams();
        if (budget !== undefined && budget !== null) {
            params = params.set('budget', String(budget));
        }
        return this.http.get(this.apiBase + '/growth_tools/youtube_ads/get_budget', {
            params,
            headers: this.authHeaders()
        });
    }

    private fetchPaidOrdersSuccess(): Observable<any> {
        return this.http.get(this.apiBase + '/growth_tools/paid-orders/success/', {
            headers: this.authHeaders()
        });
    }

    private locationSuggestions$: Observable<LocationSuggestion[]>;

    getLocationSuggestions(): Observable<LocationSuggestion[]> {
        if (!this.locationSuggestions$) {
            this.locationSuggestions$ = this.api.getCountries().pipe(
                switchMap((countries: any[]) => {
                    const suggestions: LocationSuggestion[] = [];
                    let indiaStates: any[] = [];
                    (countries || []).forEach((c: any) => {
                        if (c?.name) {
                            suggestions.push({ name: c.name, type: 'country' });
                        }
                        const states: any[] = c?.states || [];
                        states
                            .filter(s => s?.name)
                            .forEach(s => suggestions.push({ name: s.name, type: 'state' }));
                        if ((c?.name || '').toLowerCase() === 'india') {
                            indiaStates = states;
                        }
                    });

                    const cityCalls: Observable<LocationSuggestion[]>[] = indiaStates
                        .filter(s => s?.id != null)
                        .map(s => this.api.getCities(s.id).pipe(
                            map((cities: any[]) => (cities || [])
                                .filter((c: any) => c?.name && c.name !== 'Other')
                                .map((c: any) => ({ name: c.name, type: 'city' } as LocationSuggestion))),
                            catchError(() => of([] as LocationSuggestion[]))
                        ));

                    if (!cityCalls.length) {
                        return of(suggestions);
                    }
                    return forkJoin(cityCalls).pipe(
                        map(cityLists => suggestions.concat(...cityLists))
                    );
                }),
                catchError(() => of([] as LocationSuggestion[])),
                shareReplay(1)
            );
        }
        return this.locationSuggestions$;
    }

    getOrder(orderId: string): Observable<Order> {
        return this.fetchOrder(orderId).pipe(
            map((res: any) => {
                if (res && typeof res === 'object' && res.success === false) {
                    throw new Error('Order not found');
                }
                return mapOrder(res);
            })
        );
    }

    getBudgetConfig(budget?: number): Observable<CampaignConfig> {
        return this.fetchBudgetConfig(budget).pipe(
            map((res: any) => mapConfig(unwrapApiResult(res)))
        );
    }

    getDolbyPrice(): Observable<number> {
        return this.fetchDolbyPrice().pipe(
            map((res: any) => {
                const data = unwrapApiResult(res);
                const price = numericField(data?.price ?? data?.dolby_price);
                return price !== undefined ? price : 5000;
            }),
            catchError(() => of(5000))
        );
    }

    getDolbyId(songSlug: string, albumSlug?: string): Observable<number> {
        return this.fetchDolbyStatus(songSlug, albumSlug).pipe(
            map((res: any) => {
                const data = unwrapApiResult(res);
                const item = Array.isArray(data) ? data[0] : data;
                const id = numericField(item?.dolby_id ?? item?.id ?? item?.dolby_atmos_id);
                if (id === undefined) {
                    throw new Error('dolby_id missing from response');
                }
                return id;
            })
        );
    }

    submitDolby(formData: FormData): Observable<any> {
        return this.postDolbySubmission(formData);
    }

    getSpotifyVideoPrice(): Observable<number> {
        return this.fetchSpotifyVideoPrice().pipe(
            map((res: any) => {
                const data = unwrapApiResult(res);
                const price = numericField(
                    data?.price
                );
                if (price === undefined) {
                    throw new Error('Spotify video price missing from response');
                }
                return price;
            })
        );
    }

    // get_distribution's spotify_video field is [] or [{spotify_video_id, status, songid}, ...] -
    // this endpoint is scoped to one song/track already, so take its first (only) entry either
    // way, whether it comes back as that array or as a single object.
    getSpotifyVideoId(songSlug: string, albumSlug?: string): Observable<number> {
        return this.fetchSpotifyVideoStatus(songSlug, albumSlug).pipe(
            map((res: any) => {
                const data = unwrapApiResult(res);
                const sv = Array.isArray(data) ? data[0] : data;
                const id = numericField(sv?.spotify_video_id);
                if (id === undefined) {
                    throw new Error('spotify_video_id missing from response');
                }
                return id;
            })
        );
    }

    submitSpotifyVideo(payload: SpotifyVideoSubmitPayload): Observable<any> {
        const formData = new FormData();
        formData.append('song_slug', payload.songSlug);
        // Always sent, even for a single - the backend expects the field present, empty
        // rather than missing entirely.
        formData.append('album_slug', payload.albumSlug || '');
        formData.append('spotify_video_id', String(payload.spotifyVideoId));
        formData.append('video_link', payload.videoLink);
        formData.append('rights_declaration', 'I own the rights to this video');
        formData.append('star_cast', payload.starCast);
        formData.append('video_rating', String(VIDEO_RATING_CODE[payload.videoRating]));
        formData.append('thumbnail_rating', String(THUMBNAIL_RATING_CODE[payload.thumbnailRating]));
        formData.append('status', 'true');
        formData.append('thumbnail', payload.thumbnailFile);

        return this.postSpotifyVideoSubmission(formData);
    }

    createCampaignOrder(payload: CampaignOrderPayload): Observable<Order> {
        return this.postCampaignOrder(payload).pipe(
            map((res: any) => {
                if (res && typeof res === 'object' && res.success === false) {
                    throw new Error('Campaign order creation failed');
                }
                return mapOrder(res);
            })
        );
    }

    getCampaigns(code?: string): Observable<Campaign[]> {
        return this.fetchCampaignReportsList(code).pipe(
            map((res: any) => unwrapList(res).map(mapCampaign))
        );
    }

    getCampaignReport(campaignId: string): Observable<CampaignReportFile[]> {
        return this.fetchCampaignReportDetail(campaignId).pipe(
            map((res: any) => mapReportFiles(res?.reports)),
            catchError(() => of([] as CampaignReportFile[]))
        );
    }

    getOrders(): Observable<Order[]> {
        return this.fetchPaidOrdersSuccess().pipe(
            map((res: any) => unwrapList(res).map(mapOrder))
        );
    }
}
